"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { subscribeToMutations } from "@/lib/api/pendingMutations";
import { Loader2 } from "lucide-react";

/**
 * App-wide busy feedback: a dimmed overlay with a spinner whenever a page
 * change OR a write request is in flight. This is THE indicator -- mounted once
 * in Providers, so nothing has to opt in.
 *
 * `loading.tsx` alone is not enough: router navigations run inside a React
 * transition and React withholds a Suspense fallback when the next page
 * resolves quickly, so fast-but-not-instant navigations produced no feedback
 * and clicks felt dead.
 *
 * Two triggers:
 *  - Any same-tab click on an in-app link, caught by one delegated listener.
 *    This is what makes it automatic for every <Link> in the app.
 *  - `triggerNavProgress()` for programmatic router.push calls, which no click
 *    listener can infer. Prefer `useAppRouter()` (lib/hooks/useAppRouter.ts)
 *    over calling this by hand -- it fires on every push and replace, so a new
 *    navigation cannot forget.
 *
 * It clears when the URL actually changes, and on a timeout so a cancelled or
 * failed navigation cannot leave the overlay stuck over the app.
 *
 * The second source is mutations: `apiFetch` counts every non-GET request in
 * lib/api/pendingMutations, and this renders from that count. That is what makes
 * a POST show the standard spinner without its call site wiring one -- dragging
 * a card to another board column used to look like a dead click for the whole
 * round trip. Unlike navigation, mutations wait out a short delay before the
 * overlay appears, since most finish fast enough that a scrim would only flash.
 * GETs are excluded on purpose: background polling would strobe the app.
 */

const NAV_START_EVENT = "navigation-progress-start";

/** Show the overlay for a navigation the click listener cannot see. */
export function triggerNavProgress() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(NAV_START_EVENT));
}

// Long enough to cover a slow page, short enough that a stuck overlay
// self-heals rather than trapping the user behind it.
const STUCK_TIMEOUT_MS = 10_000;

// A mutation has to outlast this before the scrim appears. Most writes land
// well inside it, and covering the app for 80ms reads as a flicker, not as
// feedback.
const MUTATION_OVERLAY_DELAY_MS = 250;

export function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(() => {
    setIsNavigating(true);
    clearTimer();
    timer.current = setTimeout(() => setIsNavigating(false), STUCK_TIMEOUT_MS);
  }, [clearTimer]);

  // The destination rendered: the navigation is done.
  useEffect(() => {
    setIsNavigating(false);
    clearTimer();
  }, [pathname, searchParams, clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  useEffect(() => {
    const handleNavStart = () => start();
    window.addEventListener(NAV_START_EVENT, handleNavStart);
    return () => window.removeEventListener(NAV_START_EVENT, handleNavStart);
  }, [start]);

  // Writes in flight, counted by apiFetch. The delay timer is armed when the
  // count goes from 0 to positive and cancelled the moment it returns to 0, so a
  // burst of quick mutations never shows anything and one slow one always does.
  const [isMutating, setIsMutating] = useState(false);
  useEffect(() => {
    let delay: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeToMutations((count) => {
      if (count > 0) {
        if (delay === null) {
          delay = setTimeout(() => setIsMutating(true), MUTATION_OVERLAY_DELAY_MS);
        }
      } else {
        if (delay !== null) {
          clearTimeout(delay);
          delay = null;
        }
        setIsMutating(false);
      }
    });
    return () => {
      if (delay !== null) clearTimeout(delay);
      unsubscribe();
    };
  }, []);

  // One delegated listener rather than per-link wiring. Everything that is not
  // an in-app, same-tab navigation is deliberately ignored: modifier clicks and
  // target=_blank open a new tab (this page never changes), downloads and
  // external hosts leave the app, and a bare hash only moves the scroll.
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      // API routes (CSV export, file downloads) are not page navigations: the
      // current page stays put, so the overlay would never clear.
      if (url.pathname.startsWith("/api/")) return;

      // Same URL: nothing will change, so nothing would clear the overlay.
      if (url.pathname === pathname && url.search === window.location.search) return;

      start();
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [pathname, start]);

  if (!isNavigating && !isMutating) return null;

  return (
    <div
      role="progressbar"
      aria-label={isNavigating ? "Loading page" : "Saving"}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-[2px]"
    >
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
